import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../lib/money';
import {
  MapPin,
  Phone,
  ExternalLink,
  Sparkles,
  Clock,
  ArrowLeft,
  CheckCircle2,
  Calendar,
  Video,
  X,
} from 'lucide-react';
import {
  useAvailability,
  useEmployees,
  useBook,
  useMe,
  useSalon,
  useStartBookingVerification,
  useConfirmBookingVerification,
  SalonService,
  SalonLocation,
  SalonProfile,
  BookingResponse,
  BookingMode,
  MarketplaceEmployee,
} from '../api/use-marketplace';
import { SiteShell, formatAddress, subdomainHashHue } from '../components/site-shell';
import { AddressAutocomplete } from '@cleverbooking-fe-monorepo/shared-ui';
import {
  BookingPaymentStep,
  BookingPayload as PaymentPayload,
  localizeBookingError,
  parseBookingError,
} from '@cleverbooking-fe-monorepo/booking-payment';
import { ServiceModeBadges } from '../components/service-mode-badges';
import { SalonHeroMedia } from '../components/salon-hero-media';
import { trackMarketplaceEvent } from '../lib/track';
import { ModeChooser } from '../components/mode-chooser';
import { TimezoneConfirm } from '../components/timezone-confirm';
import { validatePhone } from '../lib/phone';
import {
  convertSlotForDisplay,
  isValidTimeZone,
  shortZoneLabel,
  wallClockToInstant,
} from '../lib/slot-timezone';

/**
 * Only allow http(s) URLs as an href. The salon website is tenant-controlled;
 * React does not block `javascript:`/`data:` in href, so a crafted value would
 * run script on click. Returns undefined (inert anchor) for anything else.
 */
function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === 'http:' || u.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * /salon/[subdomain] profile page. Cover hero, locations, services grouped
 * by platform category. Booking CTA opens a placeholder dialog until
 * Slice 1c lands — link goes to the tenant's existing public site as a
 * fallback so customers aren't dead-ended.
 */
export function SalonPage() {
  const { t } = useTranslation();
  const { subdomain } = useParams<{ subdomain: string }>();
  const { data, isLoading, error } = useSalon(subdomain);

  if (isLoading) return <SiteShell><LoadingSkeleton /></SiteShell>;
  if (error || !data) return <SiteShell><NotFoundState /></SiteShell>;

  const hash = subdomainHashHue(data.subdomain);

  return (
    <SiteShell>
      {/* ── Cover hero ─────────────────────────────────────────────── */}
      <section
        className="relative w-full aspect-[16/7] md:aspect-[21/7] overflow-hidden gradient-placeholder"
        style={{ ['--gp-h' as never]: hash }}
      >
        <SalonHeroMedia
          name={data.name}
          photos={data.photos}
          coverImageUrl={data.coverImageUrl}
          coverImagePosition={data.coverImagePosition}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        <div className="absolute top-4 left-4 md:top-6 md:left-6">
          <Link
            to="/search"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/90 backdrop-blur-sm text-sm font-medium text-foreground border border-border/40 hover:border-foreground/30 hover:shadow-sm transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('salon.back')}
          </Link>
        </div>
      </section>

      {/* ── Header card (overlapping the hero) ─────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 -mt-16 md:-mt-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl bg-card border border-border/60 p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
        >
          {data.featured && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground text-background text-[11px] font-semibold tracking-wide mb-3">
              <Sparkles className="w-3 h-3" />
              {t('salon.featured')}
            </span>
          )}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                {data.name}
              </h1>
              {data.bio && (
                <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">
                  {data.bio}
                </p>
              )}
            </div>
            {data.website && (
              <a
                href={safeHref(data.website)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-background text-foreground hover:bg-secondary text-sm font-semibold transition-colors whitespace-nowrap"
              >
                {t('salon.visitWebsite')}
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </motion.div>
      </section>

      {/* ── Locations + services ───────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-[1fr,300px] gap-12">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {t('salon.servicesHeading')}
          </h2>
          <p className="text-muted-foreground mb-8">
            {t('salon.servicesSubheading')}
          </p>

          {data.serviceCategories.length === 0 && (
            <p className="text-muted-foreground">{t('salon.noServices')}</p>
          )}

          <div className="space-y-10">
            {data.serviceCategories.map((cat) => (
              <div key={cat.key}>
                <h3 className="text-base font-bold text-foreground mb-4 pb-3 border-b border-border/40">
                  {cat.displayName}
                </h3>
                <ul className="divide-y divide-border/40">
                  {cat.services.map((svc) => (
                    <li key={svc.id}>
                      <ServiceRow service={svc} salon={data} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar — locations */}
        <aside className="lg:sticky lg:top-24 self-start">
          <h2 className="text-lg font-bold text-foreground mb-4">
            {data.locations.length === 1
              ? t('salon.location')
              : t('salon.locations', { count: data.locations.length })}
          </h2>
          <div className="space-y-4">
            {data.locations.map((loc) => (
              <LocationCard key={loc.id} location={loc} />
            ))}
          </div>
        </aside>
      </section>
    </SiteShell>
  );
}

// ─── Service row + booking dialog ──────────────────────────────────────

function ServiceRow({ service, salon }: { service: SalonService; salon: SalonProfile }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5 flex items-start justify-between gap-4 sm:gap-6">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-foreground">{service.name}</p>
        {service.description && (
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {service.description}
          </p>
        )}
        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {t('salon.minutes', { minutes: service.durationMinutes })}
          </span>
          {service.requiresDeposit && service.depositAmount != null && (
            <span>· {t('salon.deposit', { amount: formatPrice(service.depositAmount, salon.currency) })}</span>
          )}
        </div>
        {/* Mode badges (MOBILE/ONLINE) — hidden for fixed-location-only services. */}
        <ServiceModeBadges service={service} />
      </div>
      <div className="flex flex-col items-end gap-3 flex-shrink-0">
        <span className="font-bold text-foreground tabular-nums text-lg">
          {formatPrice(service.price, salon.currency)}
        </span>
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            trackMarketplaceEvent('BOOKING_STARTED', salon.subdomain);
          }}
          className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-xl bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
        >
          {t('salon.book')}
        </button>
      </div>

      {open && (
        <BookingDialog
          service={service}
          salon={salon}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Booking dialog ────────────────────────────────────────────────────
// Pick a location → date → slot → (guest fields if not signed in). Slots
// come from /marketplace/salon/{sub}/availability which proxies the same
// SlotCalculator as the per-tenant booking page, so the salon can never
// over-allocate.

function BookingDialog({
  service,
  salon,
  onClose,
}: {
  service: SalonService;
  salon: SalonProfile;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const book = useBook(salon.subdomain);
  const [locationId, setLocationId] = useState<string | null>(salon.locations[0]?.id ?? null);

  // Compute available modes from tenant + service flags. ONLINE requires both
  // service.onlineEligible AND tenant.onlineBookingsEnabled (BE re-validates).
  // Mirrors client-booking's getAvailableModes(): a service that is
  // mobile/online-eligible and NOT tied to any fixed location (empty
  // locationIds) is the admin's "alternative-mode-only" signal, so AT_LOCATION
  // drops out. Previously the marketplace defaulted such a service to "at the
  // salon" while the tenant's own booking page offered MOBILE only — the same
  // service booked differently depending on the entry point (2026-08-26).
  const mobileOn = !!service.mobileEligible;
  const onlineOn = !!service.onlineEligible && !!salon.onlineBookingsEnabled;
  const explicitlyAtLocation =
    Array.isArray(service.locationIds) && service.locationIds.length > 0;
  const alternativeOnly = (mobileOn || onlineOn) && !explicitlyAtLocation;
  const availableModes: BookingMode[] = [];
  if (salon.locations.length > 0 && !alternativeOnly) availableModes.push('AT_LOCATION');
  if (mobileOn) availableModes.push('MOBILE');
  if (onlineOn) availableModes.push('ONLINE');
  if (availableModes.length === 0) availableModes.push('AT_LOCATION');

  // Default: the first available mode (AT_LOCATION when offered, else MOBILE,
  // else ONLINE — the push order above).
  const defaultMode: BookingMode = availableModes[0];

  const [bookingMode, setBookingMode] = useState<BookingMode>(defaultMode);
  // The salon's timezone, used to translate ONLINE slot times into the
  // customer's zone for display. ONLINE availability comes back as salon-local
  // "HH:mm" (SlotCalculator uses the salon's working hours), so we need this
  // to show the customer their own local time. Use ONLY the selected location's
  // tz — NOT a fallback to some other branch's tz: a multi-branch salon whose
  // picked location has no tz would otherwise be converted with an unrelated
  // branch's offset (wrong by an hour at DST-differing branches). When the
  // selected location has no tz, leave it null so SlotPicker shows the grid as
  // salon-local with a "salon's local time" label rather than a wrong time.
  // When NO location is selected at all (pure-ONLINE / pure-MOBILE salons —
  // their hidden dispatch row is no longer exposed in `locations`), fall back
  // to the tenant-level tz the BE resolves for the salon.
  const salonTimeZone =
    salon.locations.find((l) => l.id === locationId)?.timezone ||
    (locationId ? null : salon.timezone || null);
  const [customerAddress, setCustomerAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  // Browser-detected IANA zone — sent on submit for ONLINE bookings (BE
  // requires it). Editable via TimezoneConfirm on the confirm step.
  const [customerTimezone, setCustomerTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  });
  // Default to today so the slot grid renders immediately. Tenant booking
  // rules may still drop today (e.g. when same-day bookings aren't allowed)
  // and the empty-state message will guide the user to pick another day.
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('');
  // null = "any available" (BE picks first qualified employee)
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const employees = useEmployees(salon.subdomain, {
    serviceId: service.id,
    locationId: locationId || null,
  });
  const availability = useAvailability(salon.subdomain, {
    date: date || null,
    serviceId: service.id,
    // ONLINE/MOBILE have no physical location — pass null. useAvailability's
    // enabled-gate allows locationless slots for those modes (mirrors the BE),
    // so no sentinel is needed.
    locationId: locationId || null,
    employeeId,
    bookingMode,
  });
  // Guest fields — only used when not signed in.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Inline phone validity error ('' = no error). Only meaningful for guests
  // (signed-in users don't enter a phone). validatePhone is permissive — it
  // rejects only obviously-invalid input, never enforces country rules.
  const [phoneError, setPhoneError] = useState('');
  // Marketing opt-in (#162). Default unticked; only sent when ticked.
  // Per spec, omitting the field means "no change" on the BE — we never
  // send `false` because that would silently downgrade existing opt-ins.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // REQUIRED cancellation-policy agreement. Gates the Book / Pay actions.
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [result, setResult] = useState<BookingResponse | null>(null);

  // ── Prepayment (Task 10 BE / Task 20 FE) ────────────────────────────────
  // When the salon has prepayment enabled, the dialog branches: the customer
  // either pays up-front via Stripe (the payment step) or — when the salon
  // allows it — pays at the location. `showPayment` flips the form into the
  // <BookingPaymentStep>. `payError` surfaces payment-step failures inline
  // (mirrors how book.error is shown).
  // Only offer online payment when the salon can actually RECEIVE it: prepayment
  // enabled AND payout-ready (Stripe Connect charges enabled). paymentsAvailable
  // is the server's effective gate; if the field is absent (older API) fall back
  // to the enabled flag so behaviour is unchanged for already-live salons.
  const paymentsAvailable = salon.prepayment?.paymentsAvailable ?? !!salon.prepayment?.enabled;
  const prepayEnabled = !!salon.prepayment?.enabled && paymentsAvailable;
  const allowPayAtLocation = !!salon.prepayment?.allowPayAtLocation;
  const amountDue = service.amountDue ?? Number(service.price);
  const prepayCurrency = salon.prepayment?.currency ?? 'EUR';
  const [showPayment, setShowPayment] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  // ── Email-OTP verification (Task 9) ─────────────────────────────────────
  // Guests (and signed-in users on an unverified email) must verify their
  // email before a booking is accepted. `gateThen` routes every submit path
  // through /start; if verification is required it flips into the inline
  // <EmailVerifyStep> (a render branch — NOT a remount, so all booking form
  // state is preserved). On confirm, the returned verificationToken is
  // threaded into whichever path the user originally chose.
  const [verifying, setVerifying] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<
    'book' | 'paynow' | 'paylocation' | null
  >(null);
  // The paynow path's actual booking happens inside <BookingPaymentStep> via
  // `paymentPayload`. Because `runActionWithToken('paynow')` flips
  // `showPayment` true in the SAME handler that just set the token, a ref
  // guarantees `paymentPayload` reads the fresh token regardless of React's
  // state-batching/render ordering (state alone could lag a render).
  const verificationTokenRef = useRef<string | null>(null);
  const startVerification = useStartBookingVerification();
  const confirmVerification = useConfirmBookingVerification();

  const signedIn = !!me.data;
  // A signed-in marketplace user may have NO name on their profile (magic-link
  // sign-up only captures email). The backend requires firstName, so when the
  // profile name is blank we still collect + send the name fields instead of
  // assuming the profile has them (which 400'd "firstName is required").
  const profileName = me.data?.fullName?.trim() ?? '';
  const needsNameFields = !signedIn || profileName === '';
  // A guest with an invalid phone must not be able to submit. Signed-in users
  // have no phone field, so phone validity never blocks them.
  const phoneBlocksSubmit = !signedIn && !validatePhone(phone);

  // Specific staff member for the summary, or null when "Any available".
  const selectedEmployee =
    employeeId != null
      ? employees.data?.find((e) => e.id === employeeId) ?? null
      : null;

  // Localized "Mon, 9 Jun" for the summary date row. date is YYYY-MM-DD; parse
  // as local midnight to avoid a TZ-shift to the previous day.
  const summaryDateLabel = date
    ? new Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).format(new Date(`${date}T00:00:00`))
    : '';

  // Price + currency for the summary. Prepay tenants show the exact amount due
  // with two decimals + ISO currency code (e.g. "30.00 EUR"); everyone else
  // shows the indicative service price in the salon's own currency (e.g. "€30",
  // "R$ 90"), matching the dialog header's serviceSummary format. The format
  // difference is intentional — prepay communicates an exact charge, non-prepay
  // an estimate.
  const summaryPriceLabel = prepayEnabled
    ? `${amountDue.toFixed(2)} ${prepayCurrency}`
    : formatPrice(service.price, salon.currency);

  // Lock body scroll while open.
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  // Prefill the phone field from the signed-in user's saved profile so they
  // don't re-type it. Only fills when the field is still empty — never
  // clobbers what the user has typed.
  useEffect(() => {
    if (me.data?.phone) {
      setPhone((cur) => (cur === '' ? me.data!.phone! : cur));
    }
  }, [me.data]);

  // Customer name for the prepayment payload. The shared lib uses a SINGLE
  // customerName; signed-in users with a profile name reuse it, everyone else
  // composes first + last. (Guests always fill the name fields; signed-in
  // users only when their profile name is blank — see needsNameFields.)
  const customerName = (
    !needsNameFields ? profileName : `${firstName} ${lastName}`.trim()
  ).trim();

  // ISO LocalDateTime (no zone) the BE BookingIntentRequest expects, e.g.
  // "2026-05-31T13:00:00". Built from the same date (YYYY-MM-DD) + time (HH:mm)
  // the plain book.mutate path sends as separate fields.
  const startTimeIso = date && time ? `${date}T${time}:00` : '';

  // Shared validation guard for both the prepay and pay-at-location paths.
  // Returns true when the form is submittable; sets the address error and
  // returns false for an incomplete mobile booking.
  const validateForm = (): boolean => {
    // Name guard. The Pay Now button is a sibling of the <form>, so the name
    // inputs' HTML5 `required` is never enforced on the paynow path — this is
    // the only gate. When needsNameFields is false the name is derived
    // server-side (signed-in user with a profile name), so we must NOT block.
    // Without this, a blank name → customerName="" → hold.customerName=null →
    // post-payment replay fails requireField(firstName) → eternal spinner
    // AFTER the card is charged.
    if (needsNameFields && (!firstName.trim() || !lastName.trim())) {
      setAddressError(t('booking.nameRequired'));
      return false;
    }
    if (bookingMode === 'MOBILE' && !customerAddress.trim()) {
      setAddressError(t('booking.address.required'));
      return false;
    }
    // AT_LOCATION bookings MUST carry a location. ONLINE sends locationId=null
    // by design; MOBILE resolves a hidden home-base on the BE. Without this
    // guard a missing locationId reaches the prepaid replay as null →
    // "locationId is required".
    if (bookingMode !== 'ONLINE' && bookingMode !== 'MOBILE' && !locationId) {
      setAddressError(t('booking.location.required'));
      return false;
    }
    setAddressError('');
    return true;
  };

  // BookingPayload for the shared <BookingPaymentStep>. Mirrors the BE
  // BookingIntentRequest: single customerName + ISO LocalDateTime startTime,
  // locationId omitted for ONLINE.
  const paymentPayload: PaymentPayload = {
    serviceId: service.id,
    locationId: bookingMode === 'ONLINE' ? null : locationId,
    employeeId: employeeId ?? undefined,
    bookingMode,
    startTime: startTimeIso,
    customerName,
    ...(!signedIn
      ? { customerEmail: email.trim(), customerPhone: phone.trim() }
      : (phone.trim() ? { customerPhone: phone.trim() } : {})),
    ...(bookingMode === 'MOBILE' ? { customerAddress: customerAddress.trim() } : {}),
    // Customer timezone — REQUIRED by the BE for ONLINE prepaid (#221 C2):
    // the webhook replay runs validateBookable, which rejects an ONLINE hold
    // with CUSTOMER_TIMEZONE_REQUIRED when it's missing. Auto-detected from the
    // browser (customerTimezone) and editable on the confirm step.
    ...(bookingMode === 'ONLINE' ? { customerTimezone } : {}),
    // i18n-resolved language so the BE localizes this booking's notifications,
    // and marketing opt-in when ticked — mirrors the plain /book path (#221 M5).
    // Only send marketingOptIn when true (omitting = "no change" on the BE).
    language: i18n.language,
    ...(marketingOptIn ? { marketingOptIn: true } : {}),
    ...(notes.trim() ? { notes: notes.trim() } : {}),
    // REQUIRED — the prepaid replay enforces policy agreement. The Pay button
    // is gated behind the policy checkbox so this is always true here.
    agreedToPolicy: true,
    // Email-OTP token (Task 9). Read from the ref so it's fresh on the render
    // that flips showPayment true — see verificationTokenRef above. Only added
    // when present; signed-in / already-verified flows leave it undefined.
    ...((verificationTokenRef.current ?? verificationToken)
      ? { verificationToken: verificationTokenRef.current ?? verificationToken ?? undefined }
      : {}),
  };

  // Maps a successful booking (plain, pay-at-location, or paid) onto the
  // existing BookingResponse-shaped success state so BookingConfirmation
  // renders identically. The payment path only knows the appointmentId +
  // bookingReference, so the remaining fields are filled from local selection.
  const bookFromPayment = (
    appointmentId: string | undefined,
    bookingReference: string | undefined,
  ) => {
    setResult({
      bookingReference: bookingReference ?? '',
      appointmentId: appointmentId ?? '',
      status: 'CONFIRMED',
      approvalRequired: false,
      salon: { subdomain: salon.subdomain, name: salon.name },
      serviceName: service.name,
      locationName:
        salon.locations.find((l) => l.id === locationId)?.name ?? '',
      startTime: startTimeIso,
      endTime: startTimeIso,
      price: amountDue,
      signedIn,
      bookingMode,
      customerTimezone: bookingMode === 'ONLINE' ? customerTimezone : null,
    });
  };

  // Pay-at-location path: a normal book.mutate with payAtLocation:true. The BE
  // skips the up-front charge and the existing confirmation UI shows on success.
  const submitPayAtLocation = (
    extra: { verificationTokenOverride?: string } = {},
  ) => {
    if (!validateForm()) return;
    setPayError(null);
    submitBooking({ payAtLocation: true, ...extra });
  };

  // Native form submit (e.g. Enter key inside a field). The action buttons are
  // now siblings of this <form>, so this only handles keyboard submit of the
  // field inputs. For prepay tenants the user must use the explicit Pay
  // now / Pay at location buttons, so a bare Enter must NOT fire a plain,
  // unpaid booking — bail when prepayment is enabled.
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prepayEnabled) return;
    // Route through the same gate as the Book button so Enter-to-submit also
    // honors the email-OTP step (gateThen runs validateForm internally).
    gateThen('book');
  };

  // Shared book.mutate caller for the plain + pay-at-location paths. `extra`
  // carries path-specific fields (e.g. payAtLocation:true).
  const submitBooking = (
    extra: { payAtLocation?: boolean; verificationTokenOverride?: string } = {},
  ) => {
    // Prefer an explicit override (fresh from confirm) over committed state.
    const token =
      extra.verificationTokenOverride ?? verificationToken ?? undefined;
    book.mutate(
      {
        serviceId: service.id,
        locationId: locationId ?? undefined,
        employeeId: employeeId ?? undefined,
        date,
        time,
        notes: notes.trim() || undefined,
        ...(extra.payAtLocation ? { payAtLocation: true } : {}),
        // Email-OTP token (Task 9) — only added when present.
        ...(token ? { verificationToken: token } : {}),
        // Pass the i18n-resolved language so the BE persists it on the
        // appointment and renders confirmation emails in this locale.
        language: i18n.language,
        // Mode-specific fields.
        bookingMode,
        ...(bookingMode === 'MOBILE' ? { customerAddress: customerAddress.trim() } : {}),
        // ONLINE requires customerTimezone — BE rejects without it.
        ...(bookingMode === 'ONLINE' ? { customerTimezone } : {}),
        // Signed-in users with a profile name: BE derives everything from the
        // session — send nothing. Guests OR signed-in users without a profile
        // name: send the collected name (+ contact for guests). The BE ignores
        // email/phone for signed-in users (session is the source of truth), so
        // sending firstName/lastName here is what unblocks the no-name case.
        ...(needsNameFields
          ? {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              ...(signedIn ? {} : { email: email.trim(), phone: phone.trim() }),
            }
          : {}),
        // Signed-in users: send the prefilled/edited phone so the BE can
        // contact them and fill-blanks write-back can persist it. Only when
        // present; never send an empty string.
        ...(signedIn && phone.trim() ? { phone: phone.trim() } : {}),
        // Only include the field when ticked; omission = preserve BE state.
        ...(marketingOptIn ? { marketingOptIn: true } : {}),
        // REQUIRED by the BE. The Book button is gated behind the policy
        // checkbox, so this is always true when this path is reachable.
        agreedToPolicy: true,
      },
      { onSuccess: (r) => setResult(r) },
    );
  };

  // Runs the originally-chosen booking path with an optional fresh token. For
  // paynow this just flips into the payment step — the token rides along via
  // `paymentPayload` (which reads verificationTokenRef), so callers MUST set
  // the ref/state before invoking this for paynow.
  const runActionWithToken = (
    action: 'book' | 'paynow' | 'paylocation',
    token?: string,
  ) => {
    if (action === 'book') {
      submitBooking(token ? { verificationTokenOverride: token } : {});
    } else if (action === 'paynow') {
      if (!validateForm()) return;
      setPayError(null);
      setShowPayment(true);
    } else {
      submitPayAtLocation(token ? { verificationTokenOverride: token } : {});
    }
  };

  // The single gate every action button routes through. Validates the form,
  // then either runs the action directly (signed-in OR already-verified) or
  // calls /start. verificationRequired===false means "no OTP needed" → run as
  // today; true → flip into the inline verify step, remembering which action
  // to resume after a successful confirm.
  const gateThen = (action: 'book' | 'paynow' | 'paylocation') => {
    if (!validateForm()) return;
    // Signed-in users never need OTP; a token already in hand means this email
    // was verified earlier in the session — skip the round-trip either way.
    if (signedIn || verificationToken) {
      runActionWithToken(action, verificationToken ?? undefined);
      return;
    }
    startVerification.mutate(
      { email: email.trim(), language: i18n.language },
      {
        onSuccess: (r) => {
          if (r.verificationRequired) {
            setPendingAction(action);
            setVerifying(true);
          } else {
            runActionWithToken(action);
          }
        },
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full md:max-w-lg max-h-[90vh] overflow-y-auto bg-background rounded-t-3xl md:rounded-2xl border border-border/40 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-dialog-title"
        aria-describedby="booking-dialog-description"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-secondary text-muted-foreground"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5" />
        </button>

        {result ? (
          <BookingConfirmation result={result} onClose={onClose} salonTimeZone={salonTimeZone} />
        ) : verifying ? (
          <EmailVerifyStep
            email={email.trim()}
            verifying={confirmVerification.isPending}
            error={
              confirmVerification.error
                ? ((confirmVerification.error as Error).message
                    ?.toLowerCase()
                    .includes('expired')
                    ? t('booking.verify.errorExpired')
                    : t('booking.verify.errorInvalid'))
                : null
            }
            onConfirm={(code) =>
              confirmVerification.mutate(
                { email: email.trim(), code },
                {
                  onSuccess: (r) => {
                    // Set the ref FIRST so paymentPayload (paynow) reads the
                    // fresh token on the render that flips showPayment true.
                    verificationTokenRef.current = r.verificationToken;
                    setVerificationToken(r.verificationToken);
                    setVerifying(false);
                    const action = pendingAction ?? 'book';
                    setPendingAction(null);
                    runActionWithToken(action, r.verificationToken);
                  },
                },
              )
            }
            onResend={() => {
              confirmVerification.reset();
              return startVerification.mutateAsync({
                email: email.trim(),
                language: i18n.language,
              });
            }}
            onEditEmail={() => {
              setVerifying(false);
              setPendingAction(null);
            }}
          />
        ) : (
          <>
            <h3 id="booking-dialog-title" className="text-2xl font-bold text-foreground mb-1.5">
              {service.name}
            </h3>
            <p id="booking-dialog-description" className="text-sm text-muted-foreground mb-6">
              {t('booking.serviceSummary', {
                minutes: service.durationMinutes,
                price: formatPrice(service.price, salon.currency),
                salon: salon.name,
              })}
            </p>

            {!signedIn && (
              <div className="rounded-xl bg-secondary/60 border border-border/40 px-4 py-3 mb-5 text-sm">
                {t('booking.haveAccountPrompt')}{' '}
                <Link
                  to={`/login?next=/salon/${salon.subdomain}`}
                  className="font-medium text-primary hover:underline underline-offset-4"
                >
                  {t('common.signIn')}
                </Link>{' '}
                {t('booking.haveAccountSuffix')}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              {/* ModeChooser — auto-hides when only one mode is available.
                  Handles AT_LOCATION / MOBILE / ONLINE based on tenant +
                  service flags. */}
              <ModeChooser
                availableModes={availableModes}
                selectedMode={bookingMode}
                onChange={(m) => {
                  setBookingMode(m);
                  setAddressError('');
                }}
                locationName={
                  salon.locations.find((l) => l.id === locationId)?.name
                }
              />

              {/* Address autocomplete — only for MOBILE. */}
              {bookingMode === 'MOBILE' && (
                <div className="space-y-1.5">
                  <label
                    htmlFor="customer-address"
                    className="text-xs uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {t('booking.address.label')}
                  </label>
                  <AddressAutocomplete
                    id="customer-address"
                    apiKey={process.env['REACT_APP_GOOGLE_MAPS_API_KEY'] ?? ''}
                    placeholder={t('booking.address.placeholder')}
                    onAddressSelect={(parsed) => {
                      setCustomerAddress(parsed.formattedAddress);
                      setAddressError('');
                    }}
                  />
                  {addressError && addressError !== t('booking.nameRequired') && (
                    <p className="text-sm text-red-600 dark:text-red-400">{addressError}</p>
                  )}
                </div>
              )}

              {/* ONLINE confirm UI — tenant tz comes from the selected location
                  (falls back to the customer tz when missing). The link-promise
                  copy sets expectations about the Meet link arriving by email. */}
              {bookingMode === 'ONLINE' && (
                <>
                  <TimezoneConfirm
                    tenantTimezone={
                      salon.locations.find((l) => l.id === locationId)?.timezone ||
                      customerTimezone
                    }
                    customerTimezone={customerTimezone}
                    onChange={setCustomerTimezone}
                  />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t('booking.confirm.online.linkPromise')}
                  </p>
                </>
              )}

              {/* Fee disclosure — shown when MOBILE and resolvedRelocationFee is set */}
              {bookingMode === 'MOBILE' && service.resolvedRelocationFee != null && (
                <div className="rounded-xl border border-border/60 bg-card p-4 text-sm space-y-2">
                  <div className="flex justify-between text-foreground">
                    <span>{t('booking.fee.serviceLabel')}</span>
                    <span>{formatPrice(service.price, salon.currency, undefined, { fractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-foreground">
                    <span>{t('booking.fee.travelLabel')}</span>
                    <span>{formatPrice(service.resolvedRelocationFee, salon.currency, undefined, { fractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-foreground border-t border-border/60 pt-2">
                    <span>{t('booking.fee.totalLabel')}</span>
                    <span>{formatPrice(Number(service.price) + Number(service.resolvedRelocationFee), salon.currency, undefined, { fractionDigits: 2 })}</span>
                  </div>
                </div>
              )}

              {salon.locations.length > 1 && (
                <Field label={t('booking.location.label')}>
                  <select
                    required
                    value={locationId ?? ''}
                    onChange={(e) => setLocationId(e.target.value)}
                    className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                  >
                    {salon.locations.map((l: SalonLocation) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </Field>
              )}

              {/* AT_LOCATION location guard error. addressError is reused for the
                  location message (validateForm sets it); the MOBILE error <p>
                  above only renders for MOBILE, so surface it here for the
                  non-MOBILE / non-ONLINE (AT_LOCATION) case. */}
              {bookingMode !== 'MOBILE' && bookingMode !== 'ONLINE' && addressError
                && addressError !== t('booking.nameRequired') && (
                <p className="text-sm text-red-600 dark:text-red-400">{addressError}</p>
              )}

              <StaffPicker
                employees={employees.data ?? []}
                loading={employees.isLoading}
                selected={employeeId}
                onSelect={(id) => {
                  setEmployeeId(id);
                  setTime(''); // availability changes per staff
                }}
              />

              <DateField
                value={date}
                onChange={(v) => {
                  setDate(v);
                  setTime(''); // slot list will refresh; clear stale choice
                }}
              />

              {date && (
                <SlotPicker
                  availability={availability}
                  selected={time}
                  onSelect={setTime}
                  date={date}
                  bookingMode={bookingMode}
                  salonTimeZone={salonTimeZone}
                  customerTimeZone={customerTimezone}
                />
              )}

              {needsNameFields && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('booking.firstName')}>
                    <input
                      required
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                    />
                  </Field>
                  <Field label={t('booking.lastName')}>
                    <input
                      required
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                    />
                  </Field>
                  {/* Name guard error (validateForm sets addressError to the
                      name message). The mode-gated addressError <p> above only
                      renders for AT_LOCATION; names are collected in ALL modes
                      (incl. ONLINE), so surface the message here so it's always
                      visible when validateForm blocks on a missing name. */}
                  {addressError === t('booking.nameRequired') && (
                    <p className="col-span-2 text-sm text-red-600 dark:text-red-400">{addressError}</p>
                  )}
                </div>
              )}
              {!signedIn && (
                <>
                  <Field label={t('booking.email')}>
                    <input
                      required
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                    />
                  </Field>
                  <Field label={t('booking.phone')}>
                    <input
                      required
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        if (phoneError) setPhoneError(''); // clear once they start fixing it
                      }}
                      onBlur={() =>
                        setPhoneError(validatePhone(phone) ? '' : t('booking.phoneInvalid'))
                      }
                      aria-invalid={!!phoneError}
                      placeholder={t('booking.phonePlaceholder')}
                      className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                    />
                    {phoneError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{phoneError}</p>
                    )}
                  </Field>
                </>
              )}
              {signedIn && (
                <Field label={t('booking.phoneSignedIn')}>
                  <input
                    type="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (phoneError) setPhoneError('');
                    }}
                    onBlur={() =>
                      setPhoneError(
                        phone.trim() === '' || validatePhone(phone)
                          ? ''
                          : t('booking.phoneInvalid'),
                      )
                    }
                    aria-invalid={!!phoneError}
                    placeholder={t('booking.phonePlaceholder')}
                    className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                  />
                  <span className="block text-xs text-muted-foreground mt-1">
                    {t('booking.phoneOptionalHint')}
                  </span>
                  {phoneError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{phoneError}</p>
                  )}
                </Field>
              )}

              <Field label={t('booking.notesOptional')}>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20 resize-none"
                />
              </Field>

              {/* Transactional reminders are NOT gated behind marketing consent — this is
                  plain informational copy, no checkbox. The marketing checkbox below is
                  promotions-only. */}
              <p className="text-xs text-muted-foreground leading-snug">
                {t('booking.transactionalNote')}
              </p>

              {/* Marketing opt-in (#162). Unticked by default; sent only when ticked. */}
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border/60 text-primary focus:ring-2 focus:ring-primary/30"
                />
                <span className="text-sm text-muted-foreground leading-snug">
                  {t('booking.marketingOptIn', 'Send me promotional emails and booking tips from salons')}
                </span>
              </label>

              {/* Cancellation policy text + REQUIRED agreement (hours default
                  to 24 — the marketplace salon profile doesn't expose the
                  tenant's configured window yet). */}
              <div className="rounded-xl border border-border/60 bg-secondary/40 p-3.5 space-y-2">
                <h4 className="text-sm font-semibold text-foreground">
                  {t('booking.cancellationPolicy')}
                </h4>
                <p className="text-xs text-muted-foreground leading-snug">
                  {t('booking.cancellationBody', { hours: 24 })}
                </p>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agreedToPolicy}
                  onChange={(e) => setAgreedToPolicy(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border/60 text-primary focus:ring-2 focus:ring-primary/30"
                />
                <span className="text-sm text-muted-foreground leading-snug">
                  {t('booking.acceptPolicy')} *
                </span>
              </label>

              {(book.error || payError) && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {payError ?? (book.error as Error)?.message}
                </p>
              )}
              {startVerification.isError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {t('booking.verify.startError')}
                </p>
              )}
            </form>

            {date && time && (
              <>
                <div className="mt-4 rounded-xl border border-border/60 bg-secondary/40 p-3.5 space-y-1.5">
                  <SummaryRow label={t('booking.summaryService')} value={service.name} />
                  <SummaryRow label={t('booking.summarySalon')} value={salon.name} />
                  <SummaryRow label={t('booking.summaryDate')} value={summaryDateLabel} />
                  <SummaryRow label={t('booking.summaryTime')} value={time} />
                  {service.durationMinutes != null ? (
                    <SummaryRow
                      label={t('booking.summaryDuration')}
                      value={`${service.durationMinutes} min`}
                    />
                  ) : null}
                  {summaryPriceLabel ? (
                    <SummaryRow label={t('booking.summaryPrice')} value={summaryPriceLabel} />
                  ) : null}
                  {selectedEmployee ? (
                    <SummaryRow
                      label={t('booking.summaryStaff')}
                      value={`${selectedEmployee.firstName} ${selectedEmployee.lastName ?? ''}`.trim()}
                    />
                  ) : null}
                </div>
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  {t('booking.trustNote')}
                </p>
              </>
            )}

            {/* ── Action area ──────────────────────────────────────────
                Rendered as a SIBLING of the field <form> (not a child) so the
                payment step's own <form> isn't nested inside it — nested
                <form>s are invalid HTML and the browser flattens them, which
                made the Stripe Pay button submit the outer form (GET reload to
                /salon?) instead of running confirmPayment.
                Three shapes, branching on the salon's prepayment config:
                • prepayment off            → single Book button (unchanged)
                • on + pay-at-location      → Pay now + Pay at location
                • on + prepay-only          → Pay now (only) */}
            <div className="mt-4">
              {showPayment ? (
                // Payment step — replaces the action buttons in place. Builds
                // a PaymentIntent for `paymentPayload`, renders the Stripe
                // Payment Element, and on success calls bookFromPayment so the
                // existing confirmation UI renders. A "back" link lets the user
                // return to the form (e.g. to pick pay-at-location instead).
                <div className="space-y-3">
                  <BookingPaymentStep
                    subdomain={salon.subdomain}
                    payload={paymentPayload}
                    amount={amountDue}
                    currency={prepayCurrency}
                    returnUrl={`${window.location.origin}/booking/complete`}
                    onBooked={bookFromPayment}
                    // The shared step surfaces the raw error text (for a
                    // booking-intent rejection that's the BE's JSON
                    // {code,message,…} body). Localize by code so the customer
                    // sees a friendly message, not raw JSON. Already-human
                    // strings (the step's own labels) pass through unchanged.
                    onError={(msg) =>
                      setPayError(localizeBookingError(parseBookingError(msg), t))
                    }
                    labels={{
                      pay: t('booking.payNow', 'Pay {amount}'),
                      processing: t('booking.processing', 'Processing…'),
                      confirming: t('booking.confirming', 'Confirming your booking…'),
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowPayment(false);
                      setPayError(null);
                    }}
                    className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t('booking.back', 'Back')}
                  </button>
                </div>
              ) : !prepayEnabled ? (
                // Non-prepay path. Now a sibling of the field <form>, so it
                // triggers booking via onClick (validate + submitBooking)
                // rather than native form submit.
                <>
                  {/* Salon intended online payment but isn't payout-ready
                      (Stripe setup incomplete): explain why the customer books
                      without paying instead of silently dropping the payment step. */}
                  {salon.prepayment?.enabled && !paymentsAvailable && (
                    <p className="mb-3 text-sm text-amber-600">
                      {t(
                        'booking.paymentUnavailable',
                        'Online payment is temporarily unavailable for this salon — you can still book now.'
                      )}
                    </p>
                  )}
                <button
                  type="button"
                  disabled={book.isPending || startVerification.isPending || !date || !time || !agreedToPolicy || phoneBlocksSubmit}
                  onClick={() => gateThen('book')}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-foreground hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed text-background font-semibold text-sm transition-colors"
                >
                  {book.isPending
                    ? t('booking.submitBooking')
                    : !date || !time
                      ? t('booking.submitIncomplete')
                      : t('booking.submitConfirm', { amount: formatPrice(service.price, salon.currency) })}
                </button>
                </>
              ) : (
                <div className="space-y-3">
                  {/* Pay now → switch into the payment step. Reuses the form
                      validation guard (mobile address) before flipping. */}
                  <button
                    type="button"
                    disabled={book.isPending || startVerification.isPending || !date || !time || !agreedToPolicy || phoneBlocksSubmit}
                    onClick={() => gateThen('paynow')}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-foreground hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed text-background font-semibold text-sm transition-colors"
                  >
                    {!date || !time
                      ? t('booking.submitIncomplete')
                      : t('booking.payNowAmount', 'Pay now ({{amount}} {{currency}})', {
                          amount: amountDue.toFixed(2),
                          currency: prepayCurrency,
                        })}
                  </button>
                  {allowPayAtLocation && (
                    <button
                      type="button"
                      disabled={book.isPending || startVerification.isPending || !date || !time || !agreedToPolicy || phoneBlocksSubmit}
                      onClick={() => gateThen('paylocation')}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-border bg-background text-foreground hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm transition-colors"
                    >
                      {book.isPending
                        ? t('booking.submitBooking')
                        : t('booking.payAtLocation', 'Pay at location')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ─── Staff picker ──────────────────────────────────────────────────────
// Renders an "Any available" chip + one chip per qualified employee.
// Hidden entirely when the salon has 0 or 1 employee qualified for this
// service+location (no meaningful choice). Selecting nothing leaves the
// BE free to pick the first eligible employee.

function StaffPicker({
  employees,
  loading,
  selected,
  onSelect,
}: {
  employees: MarketplaceEmployee[];
  loading: boolean;
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div>
        <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2 inline-block">
          {t('booking.staff')}
        </span>
        <div className="flex gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-9 w-24 rounded-full bg-muted/60 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // No choice to make → don't render at all. BE auto-assigns.
  if (employees.length <= 1) return null;

  return (
    <div>
      <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2 inline-block">
        {t('booking.staff')}
      </span>
      <div className="flex flex-wrap gap-2">
        <Chip active={selected === null} onClick={() => onSelect(null)}>
          {t('booking.anyStaff')}
        </Chip>
        {employees.map((e) => (
          <Chip
            key={e.id}
            active={selected === e.id}
            onClick={() => onSelect(e.id)}
          >
            {[e.firstName, e.lastName].filter(Boolean).join(' ').trim() || '—'}
          </Chip>
        ))}
      </div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center justify-center px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border',
        active
          ? 'bg-foreground text-background border-foreground'
          : 'bg-card text-foreground border-border hover:border-foreground/40',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ─── Slot picker ───────────────────────────────────────────────────────

function SlotPicker({
  availability,
  selected,
  onSelect,
  date,
  bookingMode,
  salonTimeZone,
  customerTimeZone,
}: {
  availability: ReturnType<typeof useAvailability>;
  selected: string;
  onSelect: (time: string) => void;
  date: string;
  bookingMode: BookingMode;
  salonTimeZone: string | null;
  customerTimeZone: string;
}) {
  const { t } = useTranslation();
  // Only ONLINE (video-call) slots need timezone translation: for
  // AT_LOCATION / MOBILE the customer physically shows up at the location, so
  // the location-local time is exactly the time they want to read. For ONLINE
  // the salon and customer can be in different zones, so we translate the
  // salon-local slot into the customer's zone — but ONLY when we have both a
  // valid salon tz and customer tz that actually differ. Otherwise we show the
  // salon-local time verbatim and label it, never a silently-converted (and
  // possibly wrong) value.
  const convertOnline =
    bookingMode === 'ONLINE' &&
    isValidTimeZone(salonTimeZone) &&
    isValidTimeZone(customerTimeZone) &&
    salonTimeZone !== customerTimeZone;
  const showSalonLocalNote =
    bookingMode === 'ONLINE' && !convertOnline && isValidTimeZone(salonTimeZone);
  const zoneNote = convertOnline
    ? t('booking.slotsInYourZone', {
        zone: shortZoneLabel(customerTimeZone as string, new Date()),
        defaultValue: 'Times shown in your timezone ({{zone}})',
      })
    : showSalonLocalNote
      ? t('booking.slotsInSalonZone', {
          zone: shortZoneLabel(salonTimeZone as string, new Date()),
          defaultValue: "Times are in the salon's local time ({{zone}})",
        })
      : '';

  if (availability.isLoading) {
    return (
      <Field label={t('booking.time')}>
        <div className="grid grid-cols-4 gap-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-9 rounded-lg bg-muted/60 animate-pulse" />
          ))}
        </div>
      </Field>
    );
  }
  if (availability.error) {
    return (
      <Field label={t('booking.time')}>
        <p className="text-sm text-red-600 dark:text-red-400">
          {t('booking.loadFailed', 'Could not load slots. Try a different date.')}
        </p>
      </Field>
    );
  }
  const slots = availability.data?.slots ?? [];
  if (slots.length === 0) {
    return (
      <Field label={t('booking.time')}>
        <div className="rounded-xl border border-dashed border-border/80 bg-background/50 p-4 text-center text-sm text-muted-foreground">
          {t('booking.noOpenings', 'No openings on this date. Try another day.')}
        </div>
      </Field>
    );
  }
  // Split into morning / afternoon / evening for skimmability. The split (and
  // the value we book) stays keyed on the SALON-LOCAL string — that's what the
  // BE returned and expects back. Only the visible label is translated.
  const morning = slots.filter((t) => t < '12:00');
  const afternoon = slots.filter((t) => t >= '12:00' && t < '17:00');
  const evening = slots.filter((t) => t >= '17:00');
  const groupProps = {
    selected,
    onSelect,
    date,
    convertOnline,
    salonTimeZone,
    customerTimeZone,
  };
  return (
    <Field label={t('booking.time')}>
      {zoneNote && (
        <p className="text-xs text-muted-foreground mb-3 -mt-1">{zoneNote}</p>
      )}
      <div className="space-y-4">
        {morning.length > 0 && (
          <SlotGroup label={t('booking.morning')} slots={morning} {...groupProps} />
        )}
        {afternoon.length > 0 && (
          <SlotGroup label={t('booking.afternoon')} slots={afternoon} {...groupProps} />
        )}
        {evening.length > 0 && (
          <SlotGroup label={t('booking.evening')} slots={evening} {...groupProps} />
        )}
      </div>
    </Field>
  );
}

function SlotGroup({
  label,
  slots,
  selected,
  onSelect,
  date,
  convertOnline,
  salonTimeZone,
  customerTimeZone,
}: {
  label: string;
  slots: string[];
  selected: string;
  onSelect: (time: string) => void;
  date: string;
  convertOnline: boolean;
  salonTimeZone: string | null;
  customerTimeZone: string;
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">{label}</p>
      <div className="grid grid-cols-4 gap-2">
        {slots.map((salonTime) => {
          const active = salonTime === selected;
          // Display the customer-zone time when converting; otherwise the raw
          // salon-local slot. The button's value/onSelect always uses the
          // salon-local string so the booking payload is unchanged.
          const converted =
            convertOnline && salonTimeZone
              ? convertSlotForDisplay(date, salonTime, salonTimeZone, customerTimeZone)
              : null;
          const shown = converted?.displayTime ?? salonTime;
          return (
            <button
              key={salonTime}
              type="button"
              onClick={() => onSelect(salonTime)}
              className={[
                'relative h-9 rounded-lg text-sm tabular-nums transition-colors',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border/60 text-foreground hover:border-primary/40 hover:text-primary',
              ].join(' ')}
            >
              {shown}
              {converted && converted.dayOffset !== 0 && (
                <sup className="ml-0.5 text-[9px] font-semibold align-super">
                  {converted.dayOffset > 0 ? '+1' : '−1'}
                </sup>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Date field ────────────────────────────────────────────────────────
// Native <input type="date"> wrapped so it picks up our token system, with
// quick-pick chips for the most common choices (today / tomorrow / +N days).
// Keeps native keyboard support + the platform date picker UI.

function DateField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const min = isoDate(today);
  const chips = [
    { key: 'today', label: t('booking.today'), date: isoDate(today) },
    { key: 'tomorrow', label: t('booking.tomorrow'), date: isoDate(addDays(today, 1)) },
    { key: 'day-after', label: shortDay(addDays(today, 2), i18n.language), date: isoDate(addDays(today, 2)) },
    { key: 'day-after-2', label: shortDay(addDays(today, 3), i18n.language), date: isoDate(addDays(today, 3)) },
  ];

  return (
    <div>
      <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2 inline-block">
        {t('booking.date')}
      </span>

      {/* Quick-pick chips. Touch-target sized; keeps the most common
          choices one tap away without opening the OS picker. */}
      <div className="flex flex-wrap gap-2 mb-2.5">
        {chips.map((c) => {
          const active = c.date === value;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onChange(c.date)}
              className={[
                'inline-flex items-center justify-center px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border',
                active
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-card text-foreground border-border hover:border-foreground/40',
              ].join(' ')}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Styled native input. The calendar glyph on the right is our own —
          the native browser glyph is hidden via the
          ::-webkit-calendar-picker-indicator opacity:0 rule we use on
          input[type=date]; clicking anywhere on the input still opens
          the platform picker. */}
      <label className="relative block cursor-pointer">
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
        </span>
        <input
          required
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          className="cb-date w-full bg-card border border-border/60 rounded-xl pl-10 pr-3.5 py-2.5 text-sm text-foreground font-medium focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 cursor-pointer"
        />
      </label>
    </div>
  );
}

function isoDate(d: Date): string {
  // Local YYYY-MM-DD (avoiding UTC drift around midnight).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function shortDay(d: Date, locale: string): string {
  // "Wed", "Thu" — locale-aware. Caller passes Dates +2 and +3 from today,
  // so this stays inside the same week without needing month/day.
  try {
    return new Intl.DateTimeFormat(locale || 'en', { weekday: 'short' }).format(d);
  } catch {
    return d.toDateString().slice(0, 3);
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1.5 inline-block">
        {label}
      </span>
      {children}
    </label>
  );
}

// ─── Email verify step (Task 9) ────────────────────────────────────────
// Inline OTP step rendered inside BookingDialog when /start reports that the
// guest's email needs verification. Self-contained: owns the 6-digit code
// input and a 60s resend cooldown. All booking form state lives in the parent
// and is preserved because this is a render branch, not a remount.

function EmailVerifyStep({
  email,
  onConfirm,
  onResend,
  onEditEmail,
  verifying,
  error,
}: {
  email: string;
  onConfirm: (code: string) => void;
  onResend: () => void | Promise<unknown>;
  onEditEmail: () => void;
  verifying: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(60);
  const [resendError, setResendError] = useState(false);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <h3 id="booking-dialog-title" className="text-xl font-bold text-foreground">{t('booking.verify.title')}</h3>
        <p id="booking-dialog-description" className="text-sm text-muted-foreground">{t('booking.verify.subtitle', { email })}</p>
      </div>
      <Field label={t('booking.verify.codeLabel')}>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={t('booking.verify.codePlaceholder')}
          className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-lg tracking-[0.4em] text-center focus:outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
        />
      </Field>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {resendError && (
        <p className="text-sm text-red-600 dark:text-red-400">{t('booking.verify.startError')}</p>
      )}
      <button
        type="button"
        disabled={verifying || code.length !== 6}
        onClick={() => onConfirm(code)}
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-foreground hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed text-background font-semibold text-sm transition-colors"
      >
        {verifying ? t('booking.verify.verifying') : t('booking.verify.verifyButton')}
      </button>
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onEditEmail}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {t('booking.verify.editEmail')}
        </button>
        <button
          type="button"
          disabled={cooldown > 0}
          onClick={() => {
            setResendError(false);
            Promise.resolve(onResend())
              .then(() => setCooldown(60))
              .catch(() => setResendError(true));
          }}
          className="text-primary disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
        >
          {cooldown > 0
            ? t('booking.verify.resendIn', { seconds: cooldown })
            : t('booking.verify.resend')}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium text-right">{value}</span>
    </div>
  );
}

function BookingConfirmation({
  result,
  onClose,
  salonTimeZone,
}: {
  result: BookingResponse;
  onClose: () => void;
  salonTimeZone: string | null;
}) {
  const { t, i18n } = useTranslation();
  const isOnline = result.bookingMode === 'ONLINE';
  const customerTz = result.customerTimezone ?? null;

  // startTime is a naive salon-local wall-clock ISO string (the BE stores it
  // as a zone-less LocalDateTime). For an ONLINE booking the customer may be
  // in another zone, so — when we have both a valid salon tz and the
  // customer's tz — reinterpret the wall clock in the salon zone to get the
  // true instant, then render it in the customer's zone with an explicit label.
  // For non-ONLINE (physical) bookings the local time IS the time they attend,
  // so we render the naive string as-is (browser-local parse is fine there).
  let whenLabel: string;
  const dateStr = result.startTime.slice(0, 10);
  const timeStr = result.startTime.slice(11, 16);
  if (
    isOnline &&
    isValidTimeZone(salonTimeZone) &&
    isValidTimeZone(customerTz) &&
    salonTimeZone !== customerTz
  ) {
    const instant = wallClockToInstant(dateStr, timeStr, salonTimeZone);
    whenLabel = instant
      ? new Intl.DateTimeFormat(i18n.language || undefined, {
          weekday: 'short', day: '2-digit', month: 'short',
          hour: '2-digit', minute: '2-digit',
          timeZone: customerTz as string,
          timeZoneName: 'short',
        }).format(instant)
      : new Date(result.startTime).toLocaleString(i18n.language || undefined, {
          weekday: 'short', day: '2-digit', month: 'short',
          hour: '2-digit', minute: '2-digit',
        });
  } else {
    whenLabel = new Date(result.startTime).toLocaleString(i18n.language || undefined, {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  }

  // Treat missing linkState on an ONLINE booking as LINK_PENDING — the BE
  // queues a delayed retry and the confirmation email will carry the URL.
  const linkReady =
    isOnline && result.linkState === 'LINK_READY' && !!result.onlineMeetingUrl;
  return (
    <div className="text-center py-2">
      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
        <CheckCircle2 className="w-7 h-7 text-primary" />
      </div>
      <h3 id="booking-dialog-title" className="text-2xl text-foreground mb-2">
        {result.approvalRequired ? t('booking.pendingTitle') : t('booking.confirmedTitle')}
      </h3>
      <p id="booking-dialog-description" className="text-sm text-muted-foreground leading-relaxed mb-5">
        {result.approvalRequired
          ? t('booking.pendingBody', { salon: result.salon.name })
          : t('booking.confirmedBody', { service: result.serviceName, salon: result.salon.name })}
      </p>
      <div className="rounded-xl bg-secondary/60 border border-border/40 p-4 mb-6 text-left text-sm space-y-1">
        <p className="flex justify-between">
          <span className="text-muted-foreground">{t('booking.reference')}</span>
          <span className="font-mono text-foreground">{result.bookingReference}</span>
        </p>
        <p className="flex justify-between">
          <span className="text-muted-foreground">{t('booking.when')}</span>
          <span className="text-foreground">{whenLabel}</span>
        </p>
        {!isOnline && (
          <p className="flex justify-between">
            <span className="text-muted-foreground">{t('booking.locationLabel')}</span>
            <span className="text-foreground">{result.locationName}</span>
          </p>
        )}
      </div>

      {/* ONLINE-specific CTA: Meet button when the link is ready,
          friendly pending copy otherwise. */}
      {isOnline && (
        <div className="mb-4">
          {linkReady ? (
            <a
              href={result.onlineMeetingUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <Video className="w-4 h-4" aria-hidden="true" />
              {t('booking.confirmationPage.online.joinButton')}
            </a>
          ) : (
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('booking.confirmationPage.online.linkPending')}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {result.signedIn ? (
          <Link
            to="/me/bookings"
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
          >
            {t('booking.seeMyBookings')}
          </Link>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('booking.emailSent')}
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

// ─── Location card ─────────────────────────────────────────────────────

function LocationCard({ location }: { location: SalonLocation }) {
  const addr = formatAddress(location.address);
  const mapUrl = location.lat && location.lng
    ? `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5">
      <p className="font-medium text-foreground mb-3">{location.name}</p>
      <div className="space-y-2.5 text-sm">
        <div className="flex items-start gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <a
            href={mapUrl}
            target="_blank"
            rel="noreferrer"
            className="hover:text-foreground transition-colors"
          >
            {addr}
          </a>
        </div>
        {location.phone && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="w-4 h-4 flex-shrink-0" />
            <a
              href={`tel:${location.phone}`}
              className="hover:text-foreground transition-colors"
            >
              {location.phone}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── States ────────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <>
      <div className="w-full aspect-[16/7] md:aspect-[21/7] bg-muted/60 animate-pulse" />
      <section className="max-w-5xl mx-auto px-6 -mt-16 md:-mt-20 relative z-10">
        <div className="rounded-2xl bg-card border border-border/60 p-6 md:p-8 shadow-sm">
          <div className="h-8 w-2/3 bg-muted/60 rounded animate-pulse mb-4" />
          <div className="h-4 w-full bg-muted/60 rounded animate-pulse mb-2" />
          <div className="h-4 w-1/2 bg-muted/60 rounded animate-pulse" />
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-6 py-12 space-y-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 bg-muted/40 rounded-lg animate-pulse" />
        ))}
      </div>
    </>
  );
}

function NotFoundState() {
  const { t } = useTranslation();
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
        {t('salon.notFoundEyebrow')}
      </p>
      <h1 className="text-3xl md:text-4xl text-foreground mb-4">
        {t('salon.notFoundTitle')}
      </h1>
      <p className="text-muted-foreground mb-8">
        {t('salon.notFoundBody')}
      </p>
      <Link
        to="/search"
        className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
      >
        {t('salon.browseAll')}
      </Link>
    </div>
  );
}
